import { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Header } from '../../common_v2/components/Header';
import { Footer } from '../../common_v2/components/Footer';
import { Acknowledgement } from './components/Acknowledgement';
import { PaymentInfo } from './components/PaymentInfo/PaymentInfo';
import { AppraisalInvoice } from './components/AppraisalInvoice/AppraisalInvoice';
import { useNavigate, useParams } from 'react-router-dom';
import { LoanType, paymentAgreement, PaymentReason, PaymentType } from '@apply/constants';
import { applicationInsights } from '../../ApplicationInsights';
import { usePaymentStore } from '../../state/paymentStore';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelBorderStyle, PanelTheme, useWaitOverlayStore } from '@apply/components';
import '../scss/_appraisalPage.scss';
import { logApplicationInsightsError } from '../../common/functions/applicationInsightFunctions';
import { BrokerClientApi } from '@apply/api/src/api/brokerClientApi';
import { AddressVerificationRequest, AuthorizationRequest, InvoiceLine, PaymentApi } from '@apply/api/src/api/PaymentApi';
import { useCalculateAppraisalInvoiceTotal } from './hooks/useCalculateAppraisalInvoiceTotal';
import { isMobileScreen } from '@apply/ui';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useBorrowerLoanType } from '../../queries/BorrowerFields/CoreFields';

export const Appraisal: React.FC = () => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { isPending } = useApplicationQuery(entryGuid);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    if (isPending && entryGuid) {
        return null;
    }

    const navigate = useNavigate();
    const { calculateAppraisalInvoiceTotal } = useCalculateAppraisalInvoiceTotal();
    const mobileScreen = isMobileScreen();
    const paymentGuid = useMemo(() => uuidv4(), []);

    const brokerClientApi = new BrokerClientApi(applicationInsights);
    const paymentApi = new PaymentApi(applicationInsights);

    const [loanType] = useBorrowerLoanType();

    const address1 = usePaymentStore((s) => s.address1);
    const address2 = usePaymentStore((s) => s.address2);
    const city = usePaymentStore((s) => s.city);
    const state = usePaymentStore((s) => s.state);
    const zip = usePaymentStore((s) => s.zip);
    const email = usePaymentStore((s) => s.email);
    const name = usePaymentStore((s) => s.name);
    const phone = usePaymentStore((s) => s.phone);
    const cardMonthYear = usePaymentStore((s) => s.cardMonthYear);
    const cardNumber = usePaymentStore((s) => s.cardNumber);
    const cardType = usePaymentStore((s) => s.cardType);

    const [appraisalInvoice, set_appraisalInvoice] = useState<InvoiceLine[]>([]);
    const [acknowledged, setAcknowledged] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);

    const showLocTerms = loanType === LoanType.SubNoteConstruction || loanType === LoanType.FixFlip;

    useEffect(() => {
        loadAppraisalInvoices();
    }, [entryGuid]);

    const loadAppraisalInvoices = async () => {
        if (!entryGuid) return;

        try {
            const response = await paymentApi.propertyAppraisal(entryGuid);

            if (response.statusCode !== 200) return;

            set_appraisalInvoice(response.result.invoiceLines);
        } catch (err) {
            logApplicationInsightsError(err as Error);
        }
    };

    const total = calculateAppraisalInvoiceTotal(appraisalInvoice);

    const payButtonClicked = async () => {
        if (!total) return;
        if (!address1 || !city || !state || !zip || !name || !phone || !email || !cardMonthYear || !cardNumber || !cardType) {
            throw new Error('Data missing to make payment');
        }

        setPaymentError('');
        setPaymentLoading(true);

        let address = address1;
        if (address2) address = `${address1} ${address2}`;

        const expirationTokens = cardMonthYear.split('/');

        const addressRequest: AddressVerificationRequest = {
            paymentGuid: paymentGuid,
            cardNumber: cardNumber.replace(/ /g, ''),
            creditCardType: cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            creditDebitType: 'CREDIT',
            address: address,
            zipCode: zip,
            amount: total,
        };

        const addressResponse = await paymentApi.verifyAddress(addressRequest);

        if (addressResponse.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError(addressResponse.errors[0]);
            return;
        }

        const response = await brokerClientApi.getIpAddress();
        if (response.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError('Could not calculate IP address.');
            return;
        }
        const ipAddress = response.result;

        const authorizationRequest: AuthorizationRequest = {
            paymentGuid: paymentGuid,
            accountNumber: cardNumber.replace(/ /g, ''),
            name,
            phone: phone,
            email: email,
            address1: address1,
            address2: address2,
            city: city,
            state: state,
            zip: zip,
            cardType: cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            amount: total,
            avsAuthResponseCode: addressResponse.result.avsResponseCode,
            paymentReason: PaymentReason.Appraisal,
            memo: `Appraisal payment for ${appraisalInvoice[0].address1} ${appraisalInvoice[0].address2}`,
            applicationId: entryGuid,
            disclaimer: paymentAgreement,
            ipAddress: ipAddress,
            paymentHistoryType: PaymentType.CreditCard,
        };

        const authorizationResponse = await paymentApi.authorization(authorizationRequest);

        if (authorizationResponse.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError('Payment failed - Please try again or contact BFI.');
            return;
        }

        if (!authorizationResponse.result.isSuccess) {
            setPaymentLoading(false);
            setPaymentError(authorizationResponse.result.errorMessage);
            return;
        }

        setPaymentLoading(false);
        setPaymentError('');
        navigate('/thankyou');
    };

    const payButtonDisabled = () => {
        const supportedCardType = cardType === 'VISA' || cardType === 'MASTERCARD';
        if (!supportedCardType) return true;

        if (!address1 || !city || !state || !zip || !name || !phone || !email) return true;

        if (!acknowledged) return true;

        if (paymentLoading) return true;

        return false;
    };

    const appraisalContent = () => {
        return (
            <>
                <h1 style={{ marginBottom: 20 }}>Appraisal Payment</h1>
                <AppraisalInvoice
                    appraisalInvoice={appraisalInvoice}
                    total={total}
                />
                <PaymentInfo paymentError={paymentError} />
                <Acknowledgement
                    setAcknowledged={setAcknowledged}
                    acknowledged={acknowledged}
                    showLocTerms={showLocTerms}
                />
                <div className='button-group'>
                    <Button
                        id='payButton'
                        theme={ButtonTheme.Primary}
                        handleClick={payButtonClicked}
                        disabled={payButtonDisabled()}
                        width='50%'
                    >
                        Submit Payment
                    </Button>
                </div>
            </>
        );
    };

    return (
        <>
            <div className='app-container'>
                <Header />
                <main>
                    <div className='content appraisal-page'>
                        {mobileScreen ? (
                            <>
                                <div style={{ width: '80%' }}>{appraisalContent()}</div>
                            </>
                        ) : (
                            <>
                                <Panel
                                    key={'appraisal-panel'}
                                    padding={50}
                                    theme={PanelTheme.White}
                                    borderRadius={PanelBorderRadius.Small}
                                    borderStyle={PanelBorderStyle.Solid}
                                    width='50%'
                                >
                                    {appraisalContent()}
                                </Panel>
                            </>
                        )}
                    </div>
                </main>
            </div>
            <Footer />
        </>
    );
};
